import { type z } from 'zod';

import { unique } from '@vestigas/utils';

import { type DeliveryNoteModel } from '../../zodDeliveryNoteModel';
import {
  type DeliveryNoteAdminWithRelatedData,
  type DeliveryNoteAssetMainOrChain,
} from '../types';

import { getAcceptedItems, getEnrichedAcceptedItems } from './getAcceptedItems';
import { getDeliveryNoteAcceptState } from './getAcceptStates';
import { getBilledItems } from './getBilledItems';
import { getCargoType } from './getCargoType';
import { getCustomData } from './getCustomData';
import { getOrderReferences } from './getOrderReferences';
import { getPermittedCostCentersData } from './getPermittedCostCentersData';
import { getPermittedSitesData } from './getPermittedSitesData';
import { getShippingMarks } from './getShippingMarks';

type DeliveryNoteBody = z.output<ReturnType<typeof DeliveryNoteModel>>;

type P = {
  assetChains: DeliveryNoteAdminWithRelatedData['assetChains'];
  costCenters: DeliveryNoteAdminWithRelatedData['bulkCostCenters'];
  customFields: DeliveryNoteAdminWithRelatedData['bulkCustomFields'];
  deliveryNoteAsset: DeliveryNoteAssetMainOrChain;
  sites: DeliveryNoteAdminWithRelatedData['bulkSites'];
  users: DeliveryNoteAdminWithRelatedData['bulkUsers'];
};

/**
 * Maps the raw delivery note data from the API and combines it with other data
 * to a format that's directly usable in the frontend.
 * This is used as one step of several to map the delivery note data.
 * @param deliveryNote - The raw delivery note data from the API
 * @returns The mapped delivery note data
 */
export const mapDeliveryNoteData = ({
  assetChains = [],
  costCenters = [],
  customFields = [],
  deliveryNoteAsset,
  sites = [],
  users = [],
}: P) => {
  if (!deliveryNoteAsset) {
    return undefined;
  }

  const isAssetChain = deliveryNoteAsset?.documentType === 'asset_chain';

  // Map the body object of the delivery note or asset chain to be able to process both with the same code.
  const assetBody: DeliveryNoteBody | undefined = isAssetChain
    ? deliveryNoteAsset?.body
    : deliveryNoteAsset?.assetState?.body;
  const acceptedItemsRaw = isAssetChain
    ? deliveryNoteAsset?.meta?.acceptEvent?.acceptedItems
    : deliveryNoteAsset?.acceptStates?.acceptItems;

  const mappedDeliveryNoteData = {
    acceptedItems: isAssetChain
      ? getEnrichedAcceptedItems(
          acceptedItemsRaw,
          deliveryNoteAsset?.signatureRole,
          deliveryNoteAsset,
          users,
        )
      : getAcceptedItems(deliveryNoteAsset, assetChains, users),

    acceptState: getDeliveryNoteAcceptState(deliveryNoteAsset),
    billedItems: getBilledItems(deliveryNoteAsset),
    billedState: deliveryNoteAsset?.billedState,
    buyer: assetBody.transaction?.agreement?.buyer,
    buyerCustomerNumber: assetBody.transaction?.settlement?.salesAccount?.id,
    buyerOrderReferences: getOrderReferences(assetBody, 'buyerOrder'),
    cargoType: getCargoType(assetBody),
    carrier: assetBody.transaction?.delivery?.shipFrom?.consignment?.carrier,
    carrierLicensePlates:
      assetBody.transaction?.delivery?.shipFrom?.consignment?.movement?.registrationId
        ?.map(({ id }) => id)
        ?.filter(Boolean) ?? [],
    carrierVehicleNumbers:
      assetBody.transaction?.delivery?.shipFrom?.consignment?.movement?.registrationId
        ?.map(({ issuerAssignedId }) => issuerAssignedId)
        ?.filter(Boolean) ?? [],
    constructionComponents: unique(
      assetBody.transaction?.logisticsPackage?.[0]?.lineItem?.map(
        (item) => item?.product?.constructionComponent,
      ) ?? [],
    ).filter(Boolean),
    constructionPlans: unique(
      assetBody.transaction?.logisticsPackage?.[0]?.lineItem?.map(
        (item) => item?.product?.constructionPlan,
      ) ?? [],
    ).filter(Boolean),
    costCenters: unique(
      assetBody.transaction?.logisticsPackage?.[0]?.lineItem?.map(
        (item) => item?.settlement?.payableAccount?.name,
      ) ?? [],
    ).filter(Boolean),
    createdOn: deliveryNoteAsset?.createdOn,
    customData: getCustomData(assetBody.header?.additionalPartyData),
    customFields,
    deliveryExecution: assetBody.transaction?.delivery?.execution,
    deliveryNoteDate: assetBody.header?.date,
    deliveryNoteNumber: assetBody.header?.id,
    deliveryType: assetBody.transaction?.agreement?.deliveryTerms?.code,
    dlnAcceptState: deliveryNoteAsset?.dlnAcceptState,
    documentType: deliveryNoteAsset?.documentType,
    driver:
      assetBody.transaction?.delivery?.shipFrom?.consignment?.movement?.driver,
    fromSiteAddress: assetBody.transaction?.delivery?.shipFrom?.tradeAddress,
    fromSiteIssuerAssignedId:
      assetBody.transaction?.delivery?.shipFrom?.tradeAddress?.issuerAssignedId,
    fromSiteName:
      assetBody.transaction?.delivery?.shipFrom?.tradeAddress?.lineOne,
    fromSiteTradeContact:
      assetBody.transaction?.delivery?.shipFrom?.tradeContact,
    id: deliveryNoteAsset?.id,
    issuer: assetBody?.header?.issuer,
    meta: deliveryNoteAsset?.meta,
    modifiedOn: deliveryNoteAsset?.modifiedOn,
    movementMeans:
      assetBody.transaction?.delivery?.shipFrom?.consignment?.movement?.means,
    originalJsonData: deliveryNoteAsset,
    permittedCostCenters: getPermittedCostCentersData(
      deliveryNoteAsset?.sgw,
      costCenters,
    ),
    permittedToSites: getPermittedSitesData(deliveryNoteAsset?.sgw, sites),
    processState: deliveryNoteAsset?.processState,
    project:
      assetBody.transaction?.delivery?.shipTo?.tradeAddress?.issuerAssignedId,
    recipient: assetBody.transaction?.delivery?.shipTo,
    seller: assetBody.transaction?.agreement?.seller,
    sellerOrderReferences: getOrderReferences(assetBody, 'sellerOrder'),
    shippingMarks: getShippingMarks(assetBody),
    supplier: assetBody.transaction?.delivery?.shipFrom,
    technicalIssuer: assetBody?.header?.technicalIssuer,
    toSiteRecipient:
      assetBody.transaction?.delivery?.shipTo?.tradeAddress?.id &&
      assetBody.transaction?.logisticsPackage?.[0]?.lineItem?.[0]?.settlement
        ?.purchaseAccount?.name
        ? assetBody.transaction?.logisticsPackage?.[0]?.lineItem?.[0]
            ?.settlement?.purchaseAccount?.name
        : undefined,
    toSiteSupplierAddress:
      assetBody.transaction?.delivery?.shipTo?.tradeAddress,
    toSiteSupplierName:
      assetBody.transaction?.delivery?.shipTo?.tradeAddress?.lineOne,
    toSiteSupplierTradeContact:
      assetBody.transaction?.delivery?.shipTo?.tradeContact,
    trader: assetBody.transaction?.agreement?.trader,
    users,

    ...((
      assetBody?.transaction?.delivery as
        | {
            note?: Array<{
              type?:
                | 'comment_note'
                | 'amount_pallet_note'
                | 'regie_time_note'
                | 'waiting_time_note';
              note?: {
                content?: {
                  comment?: unknown;
                  amount?: unknown;
                  time?: unknown;
                };
              };
            }>;
          }
        | undefined
    )?.note?.reduce?.(
      (
        accumulator: {
          comments: string[];
          palletAmount: number;
          regieTime: number;
          waitingTime: number;
        },
        note,
      ) => {
        switch (note.type) {
          case 'comment_note': {
            if (note.note?.content?.comment?.trim()) {
              accumulator.comments.push(note.note.content.comment.trim());
            }

            break;
          }

          case 'amount_pallet_note': {
            if (typeof note.note?.content?.amount === 'number') {
              accumulator.palletAmount = note.note.content.amount;
            }

            break;
          }

          case 'regie_time_note': {
            if (typeof note.note?.content?.time === 'number') {
              accumulator.regieTime = note.note.content.time;
            }

            break;
          }

          case 'waiting_time_note': {
            if (typeof note.note?.content?.time === 'number') {
              accumulator.waitingTime = note.note.content.time;
            }

            break;
          }
        }

        return accumulator;
      },
      {
        comments: [] as string[],
        palletAmount: 0,
        regieTime: 0,
        waitingTime: 0,
      },
    ) ?? {
      comments: [] as string[],
      palletAmount: 0,
      regieTime: 0,
      waitingTime: 0,
    }),
  };

  return mappedDeliveryNoteData;
};
